import util from '../lib/util';
import * as DateUtil from '../lib/dateutils';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
export type TaskSeriesTemplate = {title: string, duration?: number, urgency?: string, impact?: string, assignedToType?: string, assignedTo?: string, tieIns?: object[], location?: string, billable?: boolean, activityTypeIds?: string[], categories?: string[], relatedTo?: string[], tags?: string[], invoiceNotes?: string};
export type TaskSeriesExternal = {provider: string, externalSeriesId: string, durationSeconds: number, details: object, overrides: object[]};
class TaskSeries extends Base implements EntityType {
    taskSeriesId: uuid;
    rrule?: string | null;
    timeZone: string;
    startTime: Date;
    allDay: boolean;
    exDates: string[];
    rDates: string[];
    template?: TaskSeriesTemplate | null;
    external?: TaskSeriesExternal | null;
    previousSeriesId?: uuid | null;
    endedOn?: Date | null;
    constructor(doc: typeof TaskSeries | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            taskSeriesId: genUUID(),
            timeZone: null,
            startTime: null,
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "taskSeries";
        // @ts-ignore
        this.taskSeriesId = doc._taskSeriesId || doc.taskSeriesId || doc.taskseriesid || genUUID();
        // @ts-ignore
        this.rrule = doc._rrule || doc.rrule || null;
        // @ts-ignore
        this.timeZone = doc._timeZone || doc.timeZone || doc.timezone || null;
        // @ts-ignore
        this.startTime = DateUtil.timestampOnly(doc._startTime) || DateUtil.timestampOnly(doc.startTime) || DateUtil.timestampOnly(doc.starttime) || null;
        // @ts-ignore
        this.allDay = util.defaultBoolean([doc.allDay, doc._allDay, doc.allday, false]);
        // @ts-ignore
        this.exDates = doc._exDates || doc.exDates || doc.exdates || [];
        // @ts-ignore
        this.rDates = doc._rDates || doc.rDates || doc.rdates || [];
        // @ts-ignore
        this.template = doc._template || doc.template || {};
        // @ts-ignore
        this.external = doc._external || doc.external || {};
        // @ts-ignore
        this.previousSeriesId = doc._previousSeriesId || doc.previousSeriesId || doc.previousseriesid || null;
        // @ts-ignore
        this.endedOn = DateUtil.timestampOnly(doc._endedOn) || DateUtil.timestampOnly(doc.endedOn) || DateUtil.timestampOnly(doc.endedon) || null;
    }
    get id(): uuid { return this.taskSeriesId; }
    addExDate(exDate: string): string {
        if (!exDate) throw new ValidationError('No nulls allowed in an array!');
        if (!this.exDates) this.exDates = [];
        this.exDates.push(exDate);
        return exDate;
    }
    updateExDate(exDate: string) {
        if (!this.exDates) this.exDates = [];
        const index = this.exDates.findIndex(item => item === exDate);
        if (index !== -1) { this.exDates[index] = exDate; }
        else this.addExDate(exDate);
    }
    removeExDate(exDate: string) {
        if (!this.exDates) return;
        let foundItems = this.exDates?.filter(item => item === exDate);
        if (foundItems && foundItems.length > 0) {
            this.exDates = this.exDates.filter(item => !(item === exDate));
        }
    }
    abandonExDates() { this.exDates = []; }
    addRDate(rDate: string): string {
        if (!rDate) throw new ValidationError('No nulls allowed in an array!');
        if (!this.rDates) this.rDates = [];
        this.rDates.push(rDate);
        return rDate;
    }
    updateRDate(rDate: string) {
        if (!this.rDates) this.rDates = [];
        const index = this.rDates.findIndex(item => item === rDate);
        if (index !== -1) { this.rDates[index] = rDate; }
        else this.addRDate(rDate);
    }
    removeRDate(rDate: string) {
        if (!this.rDates) return;
        let foundItems = this.rDates?.filter(item => item === rDate);
        if (foundItems && foundItems.length > 0) {
            this.rDates = this.rDates.filter(item => !(item === rDate));
        }
    }
    abandonRDates() { this.rDates = []; }
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique ID for a task series"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}]; }
    toLowerCase() { return this.taskSeriesId; }
    static override getNonRefArrayFields(): string[] { return ["exDates", "rDates"]; }
    static override getDefaultEntries(): TaskSeries[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'taskSeries',
    dataArea: 'Tasks and Time',
    table: 'TASK_SERIES',
    comment: 'A recurring series of Tasks (OWA-1314). The series holds an RFC 5545 recurrence rule, its time zone and the start of its first occurrence; the Automation server expands the rule over a rolling horizon and creates one Task per occurrence, each linked back by seriesId and recurrenceId. Editing "this and following" ends a series (UNTIL) and starts a new one, so a series is never rewritten under Tasks that already exist.',
    fields: {
        taskSeriesId: {
            type: 'uuid', required: true, pk: true, default: '__UUID__', comment: 'Unique ID for a task series'
        },

        rrule: {
            type: 'string',
            comment: 'The RFC 5545 RRULE value without the "RRULE:" prefix (for example FREQ=WEEKLY;BYDAY=MO,WE), or empty for a series made only of RDATEs. UNTIL, when present, is in UTC (ends with Z). A COUNT counts from startTime.'
        },

        timeZone: {
            type: 'string', required: true, comment: 'IANA time zone name (for example America/Chicago) in which the rule is evaluated, so a 9:00 occurrence stays at 9:00 on the wall clock across daylight saving changes'
        },

        startTime: {
            type: 'timestamp', required: true, comment: 'The start of the first occurrence (DTSTART). Its wall-clock time in timeZone is the time of every occurrence'
        },

        allDay: {
            type: 'boolean', default: false, comment: 'Are the occurrences all-day (no time of day)?'
        },

        exDates: {
            type: 'string[]', comment: 'Occurrences removed from the series (EXDATE), each the original start as an RFC 5545 UTC date-time such as 20261109T150000Z'
        },

        rDates: {
            type: 'string[]', comment: 'Extra occurrences added to the series (RDATE), each as an RFC 5545 UTC date-time such as 20261109T150000Z'
        },

        template: {
            type: 'json',
            jsdoc: '{title: string, duration?: number, urgency?: string, impact?: string, assignedToType?: string, assignedTo?: string, tieIns?: object[], location?: string, billable?: boolean, activityTypeIds?: string[], categories?: string[], relatedTo?: string[], tags?: string[], invoiceNotes?: string}',
            comment: 'The Task fields every occurrence is created with. The start and due time of each Task come from the occurrence'
        },

        external: {
            type: 'json',
            jsdoc: '{provider: string, externalSeriesId: string, durationSeconds: number, details: object, overrides: object[]}',
            comment: 'Set on a series that came from Google or Outlook: the provider, its id for the series, the length of an occurrence, the shared title and location, and the provider\'s exceptions (moved, edited or listed instances with their provider ids). Rolling materialization applies them, so later occurrences keep their edits and links'
        },

        previousSeriesId: {
            type: 'uuid',
            entityType: 'taskSeries',
            comment: 'The series this one continues, set when "this and following" ended the earlier series and started this one'
        },

        endedOn: {
            type: 'timestamp', comment: 'Set when the series is ended or replaced; no occurrence is created after it'
        },
    },
    maps: [
        {
            name: 'previousSeriesId',
            keysFn: s => s.previousSeriesId,
            presentFn: (s, k) => s.previousSeriesId === k && s.status !== 'D'
        }
    ]
});
    }
}
export default TaskSeries;
